// pages/Clients/GPSModal.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import moment from 'moment-timezone';
import RoutePlay from './routeplay';

const MAPBOX_TOKEN = "pk.eyJ1IjoiYWxjb3Jha2UiLCJhIjoiY2tydnJub2JpMDllajJwb3h2NjdjbjFkdSJ9.6niQ3lJJ1OVEBMKj_BvFkA";

mapboxgl.accessToken = MAPBOX_TOKEN;

// Helper function to parse time from API
const parseApiTime = (timeString, useDirectTime = false) => {
  if (!timeString) return null;
  
  const formats = [
    'MM/DD/YYYY HH:mm:ss',
    'MM/DD/YYYY H:mm:ss',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss'
  ];
  
  for (const format of formats) {
    const parsed = useDirectTime
      ? moment(timeString, format)
      : moment.tz(timeString, format, 'GMT');
    if (parsed.isValid()) {
      return parsed;
    }
  }
  
  const defaultParsed = useDirectTime
    ? moment(timeString)
    : moment.tz(timeString, 'GMT');
  if (defaultParsed.isValid()) {
    return defaultParsed;
  }
  
  return null;
};

const formatLastUpdate = (before, useDirectTime = false) => {
  const present = useDirectTime ? moment() : moment.tz('GMT');

  if (!before || !before.isValid()) {
    return 'Invalid time format';
  }

  const diffMs = Math.max(present.diff(before), 0);
  const diffHours = Math.floor(moment.duration(diffMs).asHours());

  if (diffHours > 24) {
    const diffDays = Math.floor(moment.duration(diffMs).asDays());
    return `${diffDays} days Ago.`;
  }

  const duration = moment.duration(diffMs);
  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.asMinutes()) - hours * 60;

  return `${hours} hour and ${minutes} minutes Ago.`;
};

const GPSModal = ({ isOpen, onClose, client }) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [lastUpdate, setLastUpdate] = useState('');
  const [locationName, setLocationName] = useState('');
  const [hasValidData, setHasValidData] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [showRoutePlay, setShowRoutePlay] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const mapContainer = useRef(null);
  const trackingIntervalRef = useRef(null);
  const mapInitialized = useRef(false);
  const currentClientId = useRef(null);
  
  // Function to clean up map resources
  const cleanupMap = useCallback(() => {
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }
    
    if (marker) {
      try { marker.remove(); } catch(e) {}
      setMarker(null);
    }
    
    if (map) {
      try { 
        map.remove(); 
      } catch(err) {}
      setMap(null);
    }
    
    if (mapContainer.current) {
      mapContainer.current.innerHTML = '';
    }
    
    mapInitialized.current = false;
  }, [map, marker]);
  
  // Reset all states when modal opens or client changes
  useEffect(() => {
    if (isOpen && client) {
      if (currentClientId.current !== client.id) {
        cleanupMap();
        setCurrentLocation(null);
        setLastUpdate('');
        setLocationName('');
        setHasValidData(false);
        setDataLoaded(false);
        setMapError(false);
        currentClientId.current = client.id;
      }
    }
  }, [isOpen, client, cleanupMap]);
  
  // Cleanup on modal close
  useEffect(() => {
    if (!isOpen) {
      cleanupMap();
    }
  }, [isOpen, cleanupMap]);
  
  const isValidCoordinate = (lat, lon) => {
    if (!lat || !lon) return false;
    if (lat === 'NA' || lon === 'NA') return false;
    if (lat === '0.000000' || lon === '0.000000') return false;
    if (parseFloat(lat) === 0 && parseFloat(lon) === 0) return false;
    return true;
  };

  const fetchLocationName = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);
      const data = await response.json();
      setLocationName(data?.features?.[0]?.place_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } catch (error) {
      console.error('Error resolving location name:', error);
      setLocationName(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    }
  }, []);
  
  // Fetch real-time ping data
  const fetchRealTimePing = useCallback(async (clientData, shouldUpdateMarker = true) => {
    if (!clientData) return null;
    
    try {
      const url = `https://alcobrakeadmin.com/v3/app/v2data/getlatestpingv22?cbid=${clientData.cbid}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Ping request failed: ${response.status}`);

      // This endpoint sometimes returns an empty body when no ping exists.
      // Parsing it as JSON throws and leaves the modal in an error state.
      const responseText = (await response.text()).trim();
      const result = responseText ? JSON.parse(responseText) : null;
      
      if (result && isValidCoordinate(result.lat, result.lon)) {
        const location = {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          time: result.gtime,
        };

        fetchLocationName(location.lat, location.lng);
        
        if (result.gtime) {
          const isA300I = String(clientData.devicemode || clientData.deviceMode || '').trim().toUpperCase() === 'A300I';
          const apiTime = parseApiTime(result.gtime, isA300I);
          
          if (apiTime && apiTime.isValid()) {
            setLastUpdate(formatLastUpdate(apiTime, isA300I));
          } else {
            setLastUpdate('Invalid time format');
          }
        }
        
        setHasValidData(true);
        
        if (shouldUpdateMarker && map && mapInitialized.current && marker) {
          marker.setLngLat([location.lng, location.lat]);
          map.panTo([location.lng, location.lat], { duration: 1000 });
        }
        
        return location;
      }
      
      setHasValidData(false);
      setLastUpdate('');
      return null;
    } catch (error) {
      console.error('Error fetching ping:', error);
      setHasValidData(false);
      setLastUpdate('');
      return null;
    }
  }, [map, marker, fetchLocationName]);
  
  const startRealTimeTracking = useCallback((clientData) => {
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
    }
    
    fetchRealTimePing(clientData, true);
    
    trackingIntervalRef.current = setInterval(async () => {
      if (isOpen && clientData && mapInitialized.current) {
        const newLocation = await fetchRealTimePing(clientData, true);
        if (newLocation) {
          setCurrentLocation(newLocation);
        }
      }
    }, 10000);
  }, [isOpen, fetchRealTimePing]);
  
  const stopRealTimeTracking = useCallback(() => {
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }
  }, []);
  
  // Initialize map with retry capability
  const initMap = useCallback(async (retryCount = 0) => {
    if (!mapContainer.current) return false;
    if (mapInitialized.current) return true;
    
    const maxRetries = 3;
    
    try {
      const mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-98.5795, 39.8283],
        zoom: 4,
      });
      
      mapInstance.addControl(new mapboxgl.NavigationControl());
      
      // Wait for map to load or error
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Map load timeout')), 15000);
        
        mapInstance.once('load', () => {
          clearTimeout(timeout);
          resolve();
        });
        
        mapInstance.once('error', (e) => {
          clearTimeout(timeout);
          reject(e);
        });
      });
      
      console.log('Map loaded successfully');
      setMap(mapInstance);
      mapInitialized.current = true;
      setMapError(false);
      setIsRetrying(false);
      
      if (currentLocation) {
        const newMarker = new mapboxgl.Marker({ color: '#2d6a4f' })
          .setLngLat([currentLocation.lng, currentLocation.lat])
          .addTo(mapInstance);
        setMarker(newMarker);
        mapInstance.flyTo({
          center: [currentLocation.lng, currentLocation.lat],
          zoom: 14,
          duration: 1500
        });
      }
      
      return true;
      
    } catch (err) {
      console.error('Error creating map:', err);
      
      // Retry logic
      if (retryCount < maxRetries) {
        console.log(`Retrying map initialization (attempt ${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return initMap(retryCount + 1);
      }
      
      setMapError(true);
      setIsRetrying(false);
      return false;
    }
  }, [currentLocation]);
  
  // Retry map initialization
  const handleRetryMap = useCallback(async () => {
    setIsRetrying(true);
    setMapError(false);
    
    // Clean up existing map resources
    if (marker) {
      try { marker.remove(); } catch(e) {}
      setMarker(null);
    }
    
    if (map) {
      try { map.remove(); } catch(e) {}
      setMap(null);
    }
    
    if (mapContainer.current) {
      mapContainer.current.innerHTML = '';
    }
    
    mapInitialized.current = false;
    
    // Small delay to ensure cleanup
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Re-initialize map
    const success = await initMap();
    
    if (success && currentLocation) {
      // Re-fetch current location data
      await fetchRealTimePing(client, true);
    }
    
    setIsRetrying(false);
  }, [map, marker, client, currentLocation, initMap, fetchRealTimePing]);
  
  // Update marker when currentLocation changes
  useEffect(() => {
    if (map && mapInitialized.current && currentLocation) {
      if (marker) {
        marker.setLngLat([currentLocation.lng, currentLocation.lat]);
      } else {
        const newMarker = new mapboxgl.Marker({ color: '#2d6a4f' })
          .setLngLat([currentLocation.lng, currentLocation.lat])
          .addTo(map);
        setMarker(newMarker);
      }
    }
  }, [currentLocation, map, marker]);
  
  // Initialize map and start tracking when modal opens
  useEffect(() => {
    if (!isOpen || !client) return;
    
    const loadData = async () => {
      // First fetch location data
      const currentLoc = await fetchRealTimePing(client, false);
      setCurrentLocation(currentLoc);
      setDataLoaded(true);
      
      // Then initialize map
      const mapSuccess = await initMap();
      
      if (mapSuccess && currentLoc) {
        startRealTimeTracking(client);
      }
    };
    
    loadData();
    
    return () => {
      stopRealTimeTracking();
    };
  }, [isOpen, client, fetchRealTimePing, initMap, startRealTimeTracking, stopRealTimeTracking]);
  
  const handleRefresh = async () => {
    const currentLoc = await fetchRealTimePing(client, true);
    setCurrentLocation(currentLoc);
  };

  const getLatestLocationUrl = () => {
    if (!currentLocation) return '';
    return `https://www.google.com/maps?q=${currentLocation.lat},${currentLocation.lng}`;
  };

  const handleShareLocation = async () => {
    const url = getLatestLocationUrl();
    if (!url) return;

    try {
      if (navigator.share) {
        await navigator.share({ title: 'Latest GPS Location', text: locationName || 'Latest GPS Location', url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        window.prompt('Copy location link', url);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('Unable to share location:', error);
    }
  };
  
  const handleOpenRoutePlay = () => {
    stopRealTimeTracking();
    setShowRoutePlay(true);
  };
  
  const handleCloseRoutePlay = () => {
    setShowRoutePlay(false);
    if (client && isOpen) {
      startRealTimeTracking(client);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="gps-modal" onClick={(e) => e.stopPropagation()}>
          <div className="gps-modal-header">
            <div>
              <h3>GPS Location - {client?.name}</h3>
              <p>{client?.regNum} • {client?.cbid}</p>
              {isRetrying && (
                <span className="tracking-badge" style={{ background: '#ff9800' }}>
                  Retrying Map...
                </span>
              )}
            </div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          
          <div className="gps-toolbar">
            <div className="gps-stats">
              <div className="gps-update-copy">
                <div className="stat">
                  <span className="stat-label">Last Update:</span>
                  <span className="stat-value">
                    {!dataLoaded ? 'Loading...' : (lastUpdate || 'No data')}
                  </span>
                </div>
                <div className="location-stat">
                  <span className="stat-label">Latest Location:</span>{' '}
                  <span className="stat-value">
                    {!dataLoaded ? 'Loading...' : (locationName || 'No data')}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="gps-controls">
              <button
                className="share-location-btn"
                onClick={handleShareLocation}
                disabled={!getLatestLocationUrl()}
                title="Share latest GPS location"
                aria-label="Share latest GPS location"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <path d="m8.6 10.6 6.8-4.1M8.6 13.4l6.8 4.1" />
                </svg>
              </button>
              <button className="routeplay-btn" onClick={handleOpenRoutePlay}>
                🗺️ View Route Playback
              </button>
              <button className="refresh-btn" onClick={handleRefresh}>
                🔄 Refresh
              </button>
            </div>
          </div>
          
          <div className="gps-map-container">
            {mapError ? (
              <div className="gps-error">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#c62828" strokeWidth="1">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>Failed to load map</p>
                <button 
                  onClick={handleRetryMap} 
                  disabled={isRetrying}
                  style={{ opacity: isRetrying ? 0.6 : 1 }}
                >
                  {isRetrying ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            ) : (
              <div ref={mapContainer} className="gps-map" />
            )}
          </div>
          
          {/* <div className="gps-modal-footer">
            <div className="legend">
              <div className="legend-item">
                <span className="legend-color current"></span>
                <span>Current Location (Live)</span>
              </div>
            </div> */}
            {/* <button className="close-btn" onClick={onClose}>Close</button> */}
          {/* </div> */}
        </div>
      </div>
      
      <RoutePlay 
        isOpen={showRoutePlay}
        onClose={handleCloseRoutePlay}
        client={client}
      />
    </>
  );
};

export default GPSModal;
